import { detectInApp } from './inapp';
import { copyText } from './clipboard';

/**
 * เปิด URL เกมโดยพยายามพาผู้ใช้ที่อยู่ใน In-App Browser ของ LINE / Telegram
 * ออกไปเปิดด้วย default browser ของเครื่องก่อน
 *
 * ถ้าไม่ได้อยู่ใน In-App Browser หรือพาออกไม่ได้ จะเรียก `proceed()` ซึ่งเป็น
 * พฤติกรรมเดิมของแต่ละหน้า (บางหน้า redirect ผ่าน /goto บางหน้า location.replace ตรง ๆ)
 * — flow เดิมจึงไม่เปลี่ยนสำหรับผู้ใช้ที่ไม่ได้มาจาก In-App Browser
 */

/**
 * แอปที่เราพยายามพาออก — จงใจจำกัดไว้แค่ LINE กับ Telegram
 *
 * detectInApp() รู้จัก facebook/instagram/tiktok/… ด้วย แต่ไม่เอามาใช้ตรงนี้
 * เพราะ `isInApp` ของมันรวม `isGenericWebView` ซึ่ง false positive กับ
 * Safari บน iOS ปกติ (UA มีทั้ง version/, mobile และ safari) — ถ้าอิงค่านั้น
 * ผู้ใช้ iPhone ทั่วไปจะโดนเด้งไปทางนี้หมด
 */
const ESCAPE_APPS: ReadonlyArray<string> = ['line', 'telegram'];

/** รอดูว่า intent:// พาออกไปสำเร็จไหม ก่อนขึ้นคำแนะนำแบบ manual */
const INTENT_TIMEOUT_MS = 1200;

const OVERLAY_ID = 'tm-external-browser-overlay';

/**
 * ต่อ query param เข้ากับ URL โดยแทรกก่อน fragment
 *
 * จงใจต่อสตริงตรง ๆ แทนการใช้ URL/URLSearchParams เพราะการ re-serialize
 * จะเข้ารหัส query เดิมใหม่ (เช่น %20 กลายเป็น +) ซึ่งทำให้ signature ของ
 * ผู้ให้บริการเกมที่คำนวณจาก query string ดิบเพี้ยนได้
 */
const withQuery = (raw: string, key: string, value: string): string => {
	const hashAt = raw.indexOf('#');
	const head = hashAt === -1 ? raw : raw.slice(0, hashAt);
	const hash = hashAt === -1 ? '' : raw.slice(hashAt);
	const sep = head.includes('?') ? '&' : '?';

	return `${head}${sep}${key}=${value}${hash}`;
};

/**
 * แปลง http(s) URL เป็น Android intent: URI ที่เปิดด้วย default browser
 * คืน null เมื่อแปลงไม่ได้ — ไม่ใช่ absolute http(s) หรือมี fragment ซึ่งชนกับ #Intent
 */
const toIntentUrl = (raw: string): string | null => {
	const m = /^(https?):\/\/(.+)$/i.exec(raw);
	if (!m || m[2].includes('#')) return null;

	return `intent://${m[2]}#Intent;scheme=${m[1].toLowerCase()};action=android.intent.action.VIEW;end`;
};

const removeOverlay = () => {
	document.getElementById(OVERLAY_ID)?.remove();
};

/**
 * คำแนะนำแบบ manual สำหรับกรณีที่บังคับไม่ได้ (Telegram บน iOS เป็นหลัก)
 *
 * เขียนด้วย DOM + inline style ล้วน ไม่ผูกกับ SweetAlert2 / Tailwind / Vue
 * เพราะถูกเรียกจากทั้ง page และ popup และต้องทำงานได้แม้ CSS ของธีมยังโหลดไม่ครบ
 */
const showManualOverlay = (url: string, appName: string, isIOS: boolean, proceed: (url: string) => void) => {
	removeOverlay();

	const hint = isIOS
		? 'แตะปุ่มเมนู (⋯ หรือ ⌄) มุมขวาบน แล้วเลือก “เปิดใน Safari” / “Open in Safari”'
		: 'แตะปุ่มเมนู (⋮) มุมขวาบน แล้วเลือก “เปิดในเบราว์เซอร์” / “Open in browser”';

	const overlay = document.createElement('div');
	overlay.id = OVERLAY_ID;
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	overlay.style.cssText = [
		'position:fixed', 'inset:0', 'z-index:2147483647',
		'display:flex', 'align-items:center', 'justify-content:center',
		'padding:16px', 'background:rgba(0,0,0,.82)',
		'font-family:inherit', '-webkit-tap-highlight-color:transparent',
	].join(';');

	const box = document.createElement('div');
	box.style.cssText = [
		'width:100%', 'max-width:22rem', 'box-sizing:border-box',
		'padding:20px', 'border-radius:16px',
		'background:#111827', 'border:1px solid #374151',
		'color:#f9fafb', 'text-align:center', 'line-height:1.6',
	].join(';');

	const title = document.createElement('p');
	title.textContent = `คุณกำลังเปิดผ่านแอป ${appName === 'line' ? 'LINE' : 'Telegram'}`;
	title.style.cssText = 'margin:0 0 8px;font-size:16px;font-weight:700;color:#fcd34d';

	const detail = document.createElement('p');
	detail.textContent = 'เบราว์เซอร์ในแอปอาจทำให้เกมแสดงผลไม่สมบูรณ์ แนะนำให้เปิดด้วยเบราว์เซอร์ของเครื่อง';
	detail.style.cssText = 'margin:0 0 14px;font-size:13px;color:#d1d5db';

	const steps = document.createElement('p');
	steps.textContent = hint;
	steps.style.cssText = 'margin:0 0 16px;font-size:13px;color:#f9fafb;background:#1f2937;border-radius:10px;padding:10px 12px';

	const btnStyle = (bg: string, fg: string) => [
		'display:block', 'width:100%', 'box-sizing:border-box',
		'margin:0 0 8px', 'padding:11px 12px',
		'border:0', 'border-radius:10px', 'cursor:pointer',
		'font-size:14px', 'font-weight:600', 'font-family:inherit',
		`background:${bg}`, `color:${fg}`,
	].join(';');

	const copyBtn = document.createElement('button');
	copyBtn.type = 'button';
	copyBtn.textContent = 'คัดลอกลิงก์เกม';
	copyBtn.style.cssText = btnStyle('#fbbf24', '#111827');
	copyBtn.addEventListener('click', async () => {
		try {
			await copyText(url);
			copyBtn.textContent = 'คัดลอกแล้ว — ไปวางในเบราว์เซอร์';
		} catch {
			copyBtn.textContent = 'คัดลอกไม่สำเร็จ กรุณาใช้เมนูของแอป';
		}
	});

	const safariBtn = document.createElement('button');
	safariBtn.type = 'button';
	safariBtn.textContent = 'ลองเปิดใน Safari';
	safariBtn.style.cssText = btnStyle('#374151', '#f9fafb');
	safariBtn.addEventListener('click', () => {
		// scheme ส่วนตัวของ Safari — ใช้ได้กับ WebView บาง build เท่านั้น
		// ถ้าไม่ได้ก็จะไม่เกิดอะไรขึ้น ผู้ใช้ยังเหลือปุ่มอื่นให้กด
		window.location.href = url.replace(/^https?:/, (m) => `x-safari-${m}`);
	});

	const continueBtn = document.createElement('button');
	continueBtn.type = 'button';
	continueBtn.textContent = 'เล่นต่อในแอปนี้';
	continueBtn.style.cssText = btnStyle('transparent', '#9ca3af') + ';text-decoration:underline;margin-bottom:0';
	continueBtn.addEventListener('click', () => {
		removeOverlay();
		proceed(url);
	});

	box.appendChild(title);
	box.appendChild(detail);
	box.appendChild(steps);
	box.appendChild(copyBtn);
	if (isIOS) box.appendChild(safariBtn);
	box.appendChild(continueBtn);
	overlay.appendChild(box);
	document.body.appendChild(overlay);
};

/**
 * อยู่ใน In-App Browser ที่เราตั้งใจพาออกหรือไม่ (LINE / Telegram)
 *
 * จุดเรียกที่ต้อง "เปิดแท็บใหม่" ต้องถามตัวนี้ **ก่อน** จอง tab ด้วย window.open
 * เพราะใน LINE/Telegram `window.open` มัก *สำเร็จ* — มันเปิด view ใหม่ในแอป
 * แล้วคืน window ที่ใช้ได้ ไม่ได้คืน null ดังนั้น "จองไม่ได้" จึงไม่ใช่เงื่อนไข
 * เดียวกับ "อยู่ใน In-App": ถ้าจองก่อนแล้วค่อย fallback มา openGameUrl สาขา
 * พาออกจะไม่เคยทำงาน และผู้ใช้ติดอยู่ในแอปเหมือนเดิม
 *
 * (flow เปิดเกมใน Game.vue ไม่เจอปัญหานี้เพราะมันเปลี่ยน location ของแท็บเดิม
 * ผ่าน /goto ไม่ต้องขอ popup จึงเรียก openGameUrl ตรง ๆ ได้เลย)
 */
export const shouldEscapeInApp = (): boolean => {
	const appName = detectInApp().appName;

	return !!appName && ESCAPE_APPS.includes(appName);
};

/**
 * ต้องเลี่ยงการจอง tab ด้วย window.open หรือไม่
 *
 * นอกจากแอปที่เราพาออก (LINE/Telegram ที่ระบุตัวได้) ยังมี WKWebView ฝังในแอปบน
 * iOS ที่ UA ไม่บอกว่าเป็นแอปอะไร — in-app browser ของ Telegram iOS เป็นตัวหลัก
 * ใน view แบบนั้น `window.open('', '_blank')` คืน window ที่ *ไม่ได้แสดงผล*
 * (ไม่ใช่ null) จองแล้ว navigate ใส่ก็เหมือนหายไปเฉย ๆ ไม่มี error ให้จับ
 * ทางเดียวที่เห็นผลคือเปิดใน view เดิม แบบเดียวกับที่ปุ่มเกมทำอยู่แล้ว
 */
export const shouldAvoidNewTab = (): boolean => shouldEscapeInApp() || detectInApp().isIOSWebView;

/**
 * เปิด URL เกม — พาออกจาก In-App Browser ถ้าทำได้ ไม่งั้นทำตาม `proceed`
 *
 * @param url     URL ที่ได้จาก game.login()
 * @param proceed พฤติกรรมเดิมของจุดเรียก (ผ่าน /goto หรือ location.replace)
 */
export const openGameUrl = (url: string, proceed: (url: string) => void): void => {
	const info = detectInApp();
	const appName = info.appName;

	if (!appName || !ESCAPE_APPS.includes(appName)) {
		proceed(url);
		return;
	}

	// Telegram Mini App — มี API ให้เปิด external browser ตรง ๆ
	const telegramWebApp = (window as any).Telegram?.WebApp;
	if (appName === 'telegram' && typeof telegramWebApp?.openLink === 'function') {
		telegramWebApp.openLink(url);
		return;
	}

	// LINE — ต่อ openExternalBrowser=1 แล้ว LINE จะเด้งออก default browser ให้เอง
	// ถ้า LINE เวอร์ชันนั้นไม่รองรับ ผลลัพธ์คือเปิดเกมใน In-App ตามเดิม (ไม่แย่ลงกว่าเดิม)
	if (appName === 'line') {
		window.location.href = withQuery(url, 'openExternalBrowser', '1');
		return;
	}

	// Telegram In-App Browser ปกติ — Android ยิง intent: ให้ระบบเลือก default browser
	const intentUrl = info.isAndroid ? toIntentUrl(url) : null;
	if (intentUrl) {
		let escaped = false;
		const onHidden = () => {
			if (document.visibilityState === 'hidden') escaped = true;
		};
		document.addEventListener('visibilitychange', onHidden);

		window.location.href = intentUrl;

		window.setTimeout(() => {
			document.removeEventListener('visibilitychange', onHidden);
			if (!escaped && document.visibilityState === 'visible') {
				showManualOverlay(url, appName, info.isIOS, proceed);
			}
		}, INTENT_TIMEOUT_MS);
		return;
	}

	// iOS (และ Android ที่แปลง intent ไม่ได้) — บังคับไม่ได้ ให้คำแนะนำแทน
	showManualOverlay(url, appName, info.isIOS, proceed);
};
